// src/components/common/FloatingActionButtons.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPhoneAlt, FaArrowUp, FaBars, FaTimes, FaWhatsapp, FaEnvelope, FaUser, FaHome } from 'react-icons/fa';
import './FloatingActionButtons.css';
import { useAuth } from '../../contexts/AuthContext';

function FloatingActionButtons() {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle');
  const phoneNumberDisplay = '07 69 29 79 63';
  const phoneNumberInternational = '+33 7 69 29 79 63';
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const handleScroll = () => {
    setShowScrollButton(window.scrollY > 300);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Défilement doux
    });
  };

  const toggleMenu = () => setIsMenuOpen(v => !v);

  const openPhoneModal = () => {
    setIsPhoneModalOpen(true);
    setCopyStatus('idle');
  };

  const closePhoneModal = () => {
    setIsPhoneModalOpen(false);
    setCopyStatus('idle');
  };

  const handleActionAndClose = (action) => {
    action();
    setIsMenuOpen(false);
  };

  const goTo = (path) => {
    navigate(path);
  };

  const sanitizedPhone = phoneNumberDisplay.replace(/\s/g, '');
  const whatsappLink = `https://wa.me/${phoneNumberInternational.replace(/\s/g, '').replace('+', '')}`;

  const copyPhone = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(phoneNumberDisplay);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = phoneNumberDisplay;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();

        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 2000);
    } catch {
      setCopyStatus('error');
      window.setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    // Nettoyage de l'écouteur au démontage du composant
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Tableau de dépendances vide pour ne l'exécuter qu'au montage/démontage

  useEffect(() => {
    if (!isPhoneModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePhoneModal();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPhoneModalOpen]);

  return (
    <>
      <button
        className="floating-call-left pulse"
        aria-label={`Contacter ${phoneNumberDisplay}`}
        title={phoneNumberDisplay}
        type="button"
        onClick={openPhoneModal}
      >
        <FaPhoneAlt />
        <span className="button-label">Téléphone</span>
      </button>

      <div className="floating-buttons">
        {showScrollButton && !isMenuOpen && (
          <button
            className="floating-button scroll-top-button scroll-top-standalone"
            onClick={scrollToTop}
            title="Retour en haut"
            aria-label="Retour en haut"
            type="button"
          >
            <FaArrowUp />
            <span className="button-label">Haut</span>
          </button>
        )}
        <div className={`secondary-buttons ${isMenuOpen ? 'open' : ''}`}>
          {showScrollButton && (
            <button
              className="floating-button scroll-top-button"
              onClick={() => handleActionAndClose(scrollToTop)}
              title="Retour en haut"
              aria-label="Retour en haut"
              type="button"
            >
              <FaArrowUp />
              <span className="button-label">Haut</span>
            </button>
          )}

          {isAdmin && (
            <button
              className="floating-button admin-button"
              onClick={() => handleActionAndClose(() => goTo('/admin'))}
              title="Admin"
              aria-label="Admin"
              type="button"
            >
              <FaHome />
              <span className="button-label">Admin</span>
            </button>
          )}

          <button
            className="floating-button user-button"
            onClick={() => handleActionAndClose(() => {
              if (user) {
                goTo('/mon-compte');
              } else {
                window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { mode: 'login' } }));
              }
            })}
            title={user ? 'Mon compte' : 'Connexion'}
            aria-label={user ? 'Mon compte' : 'Connexion'}
            type="button"
          >
            <FaUser />
            <span className="button-label">{user ? 'Compte' : 'Connexion'}</span>
          </button>

          <a
            href={whatsappLink}
            className="floating-button whatsapp-button"
            aria-label="WhatsApp"
            title="WhatsApp"
            target="_blank"
            rel="noreferrer"
            onClick={() => setIsMenuOpen(false)}
          >
            <FaWhatsapp />
            <span className="button-label">WhatsApp</span>
          </a>

          <button
            className="floating-button contact-button"
            onClick={() => handleActionAndClose(() => goTo('/contact'))}
            title="Contact"
            aria-label="Contact"
            type="button"
          >
            <FaEnvelope />
            <span className="button-label">Contact</span>
          </button>
        </div>

        <button
          className={`floating-button main-button ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          title={isMenuOpen ? 'Fermer' : 'Menu'}
          aria-label={isMenuOpen ? 'Fermer' : 'Menu'}
          type="button"
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
          <span className="button-label">{isMenuOpen ? 'Fermer' : 'Menu'}</span>
        </button>
      </div>

      {isPhoneModalOpen && (
        <div className="phone-modal-overlay" onClick={closePhoneModal}>
          <div className="phone-modal" onClick={(e) => e.stopPropagation()}>
            <div className="phone-modal-header">
              <h2>Téléphone</h2>
              <button className="phone-modal-close" type="button" onClick={closePhoneModal} aria-label="Fermer">
                <FaTimes />
              </button>
            </div>

            <p className="phone-modal-subtitle">Appelez-nous maintenant</p>
            <div className="phone-modal-number-badge">{phoneNumberDisplay}</div>

            <div className="phone-modal-actions">
              <a
                className="phone-modal-action phone-modal-action-primary phone-action-call"
                href={`tel:${sanitizedPhone}`}
                onClick={closePhoneModal}
              >
                Appeler
              </a>

              <a
                className="phone-modal-action phone-modal-action-secondary phone-action-whatsapp"
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                onClick={closePhoneModal}
              >
                WhatsApp
              </a>

              <button className="phone-modal-action phone-modal-action-secondary phone-action-copy" type="button" onClick={copyPhone}>
                {copyStatus === 'copied' ? 'Copié' : copyStatus === 'error' ? 'Erreur' : 'Copier'}
              </button>

              <button
                className="phone-modal-action phone-modal-action-secondary phone-action-contact"
                type="button"
                onClick={() => {
                  closePhoneModal();
                  goTo('/contact');
                }}
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FloatingActionButtons;